import {
    CurrencyCode,
    findCurrencySymbolByCode,
    formatCurrency,
    formatNumber,
    formatNumberForDimensions,
    getCurrencyFromRegion,
    getDimensionUnit,
    getPricingUnit,
    getProductNameFields,
    getRegion,
    getTradeUnit,
    ListingBuyerModel,
    TranslateFunc,
} from '@steelbuy/ts-shared';

const productNameFieldMap = {
    definition: (listing: ListingBuyerModel, t: TranslateFunc) =>
        'definition' in listing.material && listing.material.definition
            ? t(`domainModel:material.definition.value.${listing.material.definition}`)
            : '',
    dimensions: (listing: ListingBuyerModel, t: TranslateFunc) => {
        const thickness = t(`domainModel:material.dimensions.units.${getDimensionUnit()}.labelWithValue`, {
            value: formatNumberForDimensions(listing.materialDimensions.thickness),
        });
        const width = t(`domainModel:material.dimensions.units.${getDimensionUnit()}.labelWithValue`, {
            value: formatNumberForDimensions(listing.materialDimensions.width),
        });
        const dimensions = [thickness, width];

        if ('length' in listing.materialDimensions && listing.materialDimensions.length) {
            dimensions.push(
                t(`domainModel:material.dimensions.units.${getDimensionUnit()}.labelWithValue`, {
                    value: formatNumberForDimensions(listing.materialDimensions.length),
                })
            );
        }
        return dimensions.join(' x ');
    },
    grade: (listing: ListingBuyerModel, t: TranslateFunc) =>
        'grade' in listing.material && listing.material.grade
            ? t(`domainModel:material.grade.value.${listing.material.grade}`)
            : '',
    product: (listing: ListingBuyerModel, t: TranslateFunc) =>
        'product' in listing.material && listing.material.product
            ? t(`domainModel:material.product.value.${listing.material.product}`)
            : '',
    finish: (listing: ListingBuyerModel, t: TranslateFunc) =>
        'finish' in listing.material && listing.material.finish
            ? t(`domainModel:material.finish.value.${listing.material.finish}`)
            : '',
    temper: (listing: ListingBuyerModel, t: TranslateFunc) =>
        'temper' in listing.material && listing.material.temper
            ? t(`domainModel:material.temper.value.${listing.material.temper}`)
            : '',
} as const;

export const getProductName = (listing: ListingBuyerModel, t: TranslateFunc) => {
    const productNameFields = getProductNameFields(listing.material) as (keyof typeof productNameFieldMap)[];
    return productNameFields
        .map((field) => {
            if (field in productNameFieldMap && productNameFieldMap[field]) {
                return productNameFieldMap[field]?.(listing, t);
            }
            return undefined;
        })
        .filter((value) => value !== undefined && value !== '')
        .join(' ');
};

type ListingTableConfig = Partial<
    Record<
        string,
        {
            getHeading: (
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                formData: Record<string, any>,
                t: TranslateFunc
            ) => string;
            mapValue?: (listing: ListingBuyerModel, t: TranslateFunc) => string | number | undefined;
            getTooltip?: (t: TranslateFunc) => string | number | undefined;
        }
    >
>;

export const listingTableConfig: ListingTableConfig = {
    productName: {
        getHeading: (formData, t) => t('application.dashboardListings.tableHeadings.productName'),
        mapValue: getProductName,
    },
    gauge: {
        getHeading: (formData, t) => t('application.dashboardListings.tableHeadings.gauge'),
        mapValue: (listing) => listing.gauge ?? '',
    },
    quality: {
        getHeading: (formData, t) => t('application.dashboardListings.tableHeadings.quality'),
        mapValue: (listing, t) => t(`domainModel:material.quality.value.${listing.prime ? 'prime' : 'nonPrime'}`),
    },
    location: {
        getHeading: (formData, t) => t('application.dashboardListings.tableHeadings.location'),
        mapValue: (listing, t) => {
            const locationTextArray = [];
            if (listing.state) {
                locationTextArray.push(t(`domainModel:address.shortState.${listing.state}`));
            }
            if (listing.country) {
                locationTextArray.push(t(`domainModel:address.shortCountry.${listing.country}`));
            }
            return locationTextArray.length ? locationTextArray.join(', ') : '';
        },
    },
    weight: {
        getHeading: (formData, t) =>
            t('application.createBulkListing.fields.weight', {
                tradeUnit: t(`domainModel:material.tradeUnit.value.${getTradeUnit(formData)}.pluralLabel`),
            }),
        mapValue: (listing) => formatNumber(listing.weight),
    },
    price: {
        getHeading: (formData, t) =>
            `${findCurrencySymbolByCode(getCurrencyFromRegion(getRegion()) as CurrencyCode)}/${t(
                `domainModel:material.pricingUnit.value.${getPricingUnit(formData)}.label`
            )}`,
        mapValue: (listing: ListingBuyerModel) => {
            if (listing?.pricing?.priceIncludingDelivery) {
                return formatCurrency(
                    listing.pricing.priceIncludingDelivery.value,
                    getCurrencyFromRegion(getRegion()) as CurrencyCode
                );
            }
            return '';
        },
        getTooltip: (t) => t('application.dashboardListings.deliveredTotal'),
    },
};
